import { Node } from '@cast/types';

import { getNodeRegion } from 'utils/node';

import { WorkloadsSelectOption } from './_components/WorkloadsSelect';
import { PreparedNode } from './types';
import { isEvictorAnnotation, isRebalanceAnnotation } from './utils';
import {
  checkBinPackingBlock,
  getNodeClassName,
  getNodeConfigurationName,
  getNodeConfigurationVersion,
  getNodePoolName,
  getNodeSelectionTemplateName,
  getNodeSelectionTemplateVersion,
  isRemovalDisabled,
} from '../_utils/common';
import { getNodeProvisioner } from '../_utils/getNodeProvisioner';
import { NODE_PROVISIONER_DISPLAY_NAMES } from '../constants/labels';

import {
  ClientSideSearchCriterion,
  makeSearchBoxCriteria,
  makeSearchBoxCriterion,
  SearchBoxCriterion,
} from '@cast/shared-search-ui';

export const nodeListId = 'node-list';

export const selectionTemplateNameCriterion = makeSearchBoxCriterion<Node>({
  key: 'Node template:',
  getValue: getNodeSelectionTemplateName,
  mapOptions: (options) => {
    return ['Blank', ...options];
  },
  matcher: (node: Node, value: string | string[]): boolean => {
    const nodeValue = getNodeSelectionTemplateName(node);

    if (Array.isArray(value)) {
      if (value.includes('Blank')) {
        return !nodeValue;
      }

      return Boolean(nodeValue && value.includes(nodeValue));
    } else {
      if (value === 'Blank') {
        return !nodeValue;
      }

      return nodeValue === value;
    }
  },
  allowMultiple: true,
});

export const selectionTemplateVersionCriterion = makeSearchBoxCriterion<Node>({
  key: 'Node template version:',
  getValue: getNodeSelectionTemplateVersion,
  allowMultiple: true,
});

export const configurationTemplateNameCriterion = makeSearchBoxCriterion<Node>({
  key: 'Configuration:',
  getValue: getNodeConfigurationName,
  mapOptions: (options) => {
    return ['Blank', ...options];
  },
  matcher: (node: Node, value: string | string[]) => {
    const nodeValue = getNodeConfigurationName(node);
    if (Array.isArray(value)) {
      if (value.includes('Blank')) {
        return !nodeValue;
      }

      return Boolean(nodeValue && value.includes(nodeValue));
    } else {
      if (value === 'Blank') {
        return !nodeValue;
      }

      return nodeValue === value;
    }
  },
  allowMultiple: true,
});

const managedByCriterion = makeSearchBoxCriterion<Node>({
  key: 'Managed by:',
  getValue: (node) => NODE_PROVISIONER_DISPLAY_NAMES[getNodeProvisioner(node)],
  getOptions: (nodes = []) => {
    const uniqueProvisioners = new Set(nodes.map(getNodeProvisioner));
    return [...uniqueProvisioners].map(
      (provisioner) => NODE_PROVISIONER_DISPLAY_NAMES[provisioner]
    );
  },
  allowMultiple: true,
});

export const configurationTemplateVersionCriterion =
  makeSearchBoxCriterion<Node>({
    key: 'Configuration version:',
    getValue: getNodeConfigurationVersion,
    allowMultiple: true,
  });

export const resourceOfferingCriterion: ClientSideSearchCriterion<Node> = {
  key: 'Resource offering:',
  prop: 'resourceOffering',
};

export const nodeNameCriterion: SearchBoxCriterion<Node> = {
  key: 'Node name:',
  prop: 'name',
  allowMultiple: true,
};

export const nodeIdCriterion: SearchBoxCriterion<Node> = {
  key: 'Node id:',
  prop: 'id',
  allowMultiple: true,
};

export const annotationsCriterion: SearchBoxCriterion<Node> = {
  key: 'Annotations:',
  prop: 'annotations',
  allowMultiple: true,
  matcher: 'exact',
  getValue: (node) => {
    if (!node.annotations) {
      return [];
    }
    const entries = Object.entries(node.annotations);
    if (!entries.length) {
      return [];
    }
    return entries.map(([key, val]) => `${key}=${val}`);
  },
};

export const labelsCriterion: SearchBoxCriterion<Node> = {
  key: 'Labels:',
  prop: 'labels',
  allowMultiple: true,
  matcher: 'exact',
  getValue: (node) => {
    const entries = Object.entries(node.labels);
    if (!entries.length) {
      return [];
    }
    return entries.map(([key, val]) => `${key}=${val}`);
  },
};

const nodeClassCriterion = makeSearchBoxCriterion<Node>({
  key: 'Node class:',
  getValue: getNodeClassName,
  matcher: 'exact',
  allowMultiple: true,
});

const statusCriterion: ClientSideSearchCriterion<Node> = {
  key: 'status',
  getValue: (node: Node) => {
    const nodeStatus = node.state.phase?.toString() || '';
    // TODO: remove when GRAV-366 is done
    if (nodeStatus === 'not-ready') {
      return 'notReady';
    }
    return nodeStatus;
  },
  matcher: 'exact',
};

const workloadsCriterion: SearchBoxCriterion<PreparedNode> = {
  key: 'workloads',
  getValue: (node) => {
    return node.workloads?.map((workload) => workload.name);
  },
  matcher: (node: PreparedNode, selectedOptions: WorkloadsSelectOption[]) => {
    return (node.workloads || []).some((nodeWorkload) =>
      selectedOptions.some(
        (selectedOption) =>
          selectedOption.name === nodeWorkload.name &&
          selectedOption.namespace === nodeWorkload.namespace
      )
    );
  },
  allowMultiple: true,
};

export const regionCriterion: SearchBoxCriterion<Node> = {
  key: 'Region:',
  getValue: (node) => getNodeRegion(node),
  matcher: 'exact',
  allowMultiple: true,
};

export const nodePoolCriterion = makeSearchBoxCriterion<Node>({
  key: 'Node pool:',
  getValue: getNodePoolName,
  matcher: 'exact',
  allowMultiple: true,
});

export enum BinPackingBlockedFilterValue {
  YES = 'Yes',
  NO = 'No',
}
export const binPackingBlockedCriterion: ClientSideSearchCriterion<PreparedNode> =
  {
    key: 'Bin packing blocked:',
    getValue: (node) => {
      const { isBinPackingBlocked } = checkBinPackingBlock(node);
      return isBinPackingBlocked
        ? BinPackingBlockedFilterValue.YES
        : BinPackingBlockedFilterValue.NO;
    },
  };

const sharedSearchBoxCriteria = makeSearchBoxCriteria<PreparedNode>(
  nodeNameCriterion,
  nodeIdCriterion,
  { key: 'Type:', prop: 'instanceType', allowMultiple: true },
  regionCriterion,
  {
    key: 'Added by:',
    prop: 'addedBy',
    allowEmptyValue: true,
    allowMultiple: true,
  },
  {
    key: 'Unschedulable:',
    getValue: (node) => (node.unschedulable ? 'Yes' : 'No'),
  },
  { key: 'Availability zone:', prop: 'zone', allowMultiple: true },
  {
    key: 'OS:',
    getValue: (node) => node.nodeInfo?.operatingSystem,
    allowMultiple: true,
  },
  {
    key: 'Rebalancer annotation:',
    getValue: (node) =>
      node.annotations
        ? Object.keys(node.annotations).filter((annotation) =>
            isRebalanceAnnotation(annotation)
          )
        : [],
    allowMultiple: true,
  },
  binPackingBlockedCriterion,
  annotationsCriterion,
  labelsCriterion
);

const sharedCriteria = [
  ...sharedSearchBoxCriteria,
  resourceOfferingCriterion,
  statusCriterion,
  workloadsCriterion,
];

export const nodeListCriteria = [
  ...sharedSearchBoxCriteria,
  ...makeSearchBoxCriteria<PreparedNode>(
    managedByCriterion,
    {
      key: 'Removal-disabled:',
      getValue: (node) => (isRemovalDisabled(node) ? 'Yes' : 'No'),
    },
    {
      key: 'Label applied:',
      getValue: (node) => (node.labels ? Object.keys(node.labels) : []),
      matcher: 'exact',
      allowMultiple: true,
    },
    { key: 'Role:', prop: 'role', allowMultiple: true },
    configurationTemplateNameCriterion,
    configurationTemplateVersionCriterion,
    selectionTemplateNameCriterion,
    selectionTemplateVersionCriterion,
    {
      key: 'Annotation applied:',
      getValue: (node) =>
        node.annotations ? Object.keys(node.annotations) : [],
      matcher: 'exact',
      allowMultiple: true,
    },
    {
      key: 'Evictor annotation:',
      getValue: (node) =>
        node.annotations
          ? Object.keys(node.annotations).filter((annotation) =>
              isEvictorAnnotation(annotation)
            )
          : [],
      allowMultiple: true,
    }
  ),
  resourceOfferingCriterion,
  statusCriterion,
  workloadsCriterion,
];

export const kentNodeListCriteria = [
  ...sharedCriteria,
  ...makeSearchBoxCriteria<PreparedNode>(nodeClassCriterion, nodePoolCriterion),
];
